/**
 * About BluStream Content
 * 
 * This module provides the content and structure for the "About BluStream" tab.
 */
import StartAgain from './start-again.js';

const aboutBluStreamContent = {
  /**
   * Generate the About BluStream content with text on the left and image on the right
   * @returns {string} HTML content for the About BluStream section
   */
  generate: function(isMarketingPath: boolean = false) {
    const ctaHref = isMarketingPath ? '"https://blustream.io/contact-sales?utm_source=pxai_journey&utm_medium=internal&utm_campaign=13746024-PXAI%20Journey%20Builder"' : 'https://blustream.ai/pricing';
    const ctaText = isMarketingPath ? 'Schedule your call →' : 'Get started →';

    return `
      <div class="flex flex-col md:flex-row w-full">
        <!-- Left side content (text) -->
        <div class="w-full md:w-1/2 pr-0 md:pr-8 flex flex-col justify-start p-0 md:p-8 md:h-auto">
          <!-- Main heading -->
          <h2 class="text-6xl font-normal text-gray-900 mb-6">Let's Get Started</h2>

          <!-- Main paragraph -->
          <p class="text-lg mb-6">Most brands are live with Polly within 2-3 weeks. Here's your path to launch:</p>

          <!-- Steps -->
          <p class="text-lg mb-6"><b>Step 1:</b> Schedule a call with our team. We'll answer your questions and take a deeper dive into the dashboard and analytics.</p>
          <p class="text-lg mb-6"><b>Step 2:</b> Sign up—it's free for up to 250 customers (no credit card required). Upload your brand voice and product guides to Polly's Vault.</p>
          <p class="text-lg mb-6"><b>Step 3:</b> Review and approve Polly's messaging, then let her fly—and watch your retention soar.</p>

          <!-- CTA Button -->
          <a href=${ctaHref} target="_blank" class="brand-button mb-6">
            <span class="inline-block text-center font-medium">${ctaText}</span>
          </a>
          
          <!-- Divider -->
          <hr class="border-gray mt-6" style="margin-bottom: 1.5rem;">
          
          <!-- Additional information -->
          <div class="text-base">
            <p><b>Check your inbox:</b> We've emailed your Polly preview so you can revisit it anytime or share it with your team.</p>
          </div>
        </div>
        
        <!-- Right side content (image) -->
        <div class="w-full md:w-1/2 mt-8 md:mt-0 flex flex-col items-center justify-center p-4">
          <img 
            src="/img/AboutBluStream.png" 
            alt="About BluStream Illustration" 
            class="max-w-full max-h-[550px] object-contain rounded-4xl shadow-lg mb-6"
          >
          
          <!-- Start Again Button -->
          <button 
            id="startAgainButton" 
            class="start-again-button"
          >
            <svg class="refresh-icon" width="25" height="24" viewBox="0 0 25 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path d="M12.5 20C10.2667 20 8.375 19.225 6.825 17.675C5.275 16.125 4.5 14.2333 4.5 12C4.5 9.76667 5.275 7.875 6.825 6.325C8.375 4.775 10.2667 4 12.5 4C13.65 4 14.75 4.23767 15.8 4.713C16.85 5.18767 17.75 5.86667 18.5 6.75V4H20.5V11H13.5V9H17.7C17.1667 8.06667 16.4377 7.33333 15.513 6.8C14.5877 6.26667 13.5833 6 12.5 6C10.8333 6 9.41667 6.58333 8.25 7.75C7.08333 8.91667 6.5 10.3333 6.5 12C6.5 13.6667 7.08333 15.0833 8.25 16.25C9.41667 17.4167 10.8333 18 12.5 18C13.7833 18 14.9417 17.6333 15.975 16.9C17.0083 16.1667 17.7333 15.2 18.15 14H20.25C19.7833 15.7667 18.8333 17.2083 17.4 18.325C15.9667 19.4417 14.3333 20 12.5 20Z"/>
            </svg>
            Try Another Product
          </button>
        </div>
      </div>
    `;
  },
  
  /**
   * Renders the About BluStream content in the specified container
   * @param {string} containerId - ID of the HTML element to render content into
   */
  render: function(containerId: string, isMarketingPath: boolean = false) {
    const container = document.getElementById(containerId);
    if (!container) {
      console.error(`Container with ID "${containerId}" not found`);
      return;
    }

    container.innerHTML = this.generate(isMarketingPath);
    
    // Set up Start Again button click handler
    const startAgainButton = container.querySelector('#startAgainButton');
    if (startAgainButton) {
      startAgainButton.addEventListener('click', this.handleStartAgain.bind(this));
    }
    
    // Use shared vertical centering utility
    if (window.verticalCenteringUtils) {
      window.verticalCenteringUtils.setupVerticalCentering(containerId, 'About BluStream');
    }
  },
  
  /**
   * Handle Start Again button click
   */
  handleStartAgain: function() {
    StartAgain.execute();
  }
};

// Make available globally
(window as any).aboutBluStreamContent = aboutBluStreamContent;